import * as React from 'react';
import styled, { CSSObject } from 'styled-components';
import { useFocusContext } from '../../core/Focus';
import { GLOBAL_INPUT_CLASS } from '../../core/Input';
import { useSetter, useGetter, Layout, slot, useEffect } from './slots';
import { ctxSelectClass } from './shared';
import { withConfig, splitClasses } from '../utils';
import { InputColors, InputConfig, InputStyleConfigP } from '../Input';
import { safeBlur } from '../../core/Env';

const Component = slot('control')(({ children, className, disabled = false }) => {

    const { focus, blur, focused, strongBlur } = useFocusContext();

    const controlRef = React.useRef<HTMLDivElement>(null);

    const selectController =    useGetter('select');
    const dropdownController =  useGetter('dropdown');
    const targetController =    useGetter('target');

    const [inactive] =          useEffect('selectInactivate');


    React.useEffect(() => {
        if(disabled){
            selectController.off();
        }
    }, [disabled]);


    const onFocus = focus(() => {
        dropdownController.open();
        selectController.on();
    });

    const onBlur = blur(() => {
        dropdownController.close();
        selectController.off();
    });

    useSetter('service', {
        controlRef: () => controlRef,
    });

    React.useEffect(() => {
        safeBlur(controlRef)();
        window.requestAnimationFrame(strongBlur);
    }, [inactive]);

    return (
        <Control
            ref={controlRef}
            tabIndex={0}
            focused={focused}
            disabled={disabled}
            onFocus={onFocus}
            onBlur={onBlur}
            isEmpty={targetController.isEmpty()}
            className={splitClasses(GLOBAL_INPUT_CLASS, ctxSelectClass('control'), className)}>
            {children}
            <Layout name="indicator" />
        </Control>
    );
});

export default Component;




interface Focusable {
    focused: boolean
}

interface Disable {
    disabled: boolean
}

interface Valueable {
    isEmpty: boolean
}

const hoverControl = ({ focused, disabled, isEmpty, cfg, theme }: Focusable & Disable & Valueable & InputStyleConfigP): CSSObject => {
    if(!focused && !disabled){

        if(isEmpty){
            return InputColors.hoverEmptyValue({ cfg, theme });
        }
        else {
            return InputColors.hover({ cfg, theme });
        }

    }

    return {};
}

const focusedControl = ({ focused, cfg, theme }: Focusable & InputStyleConfigP): CSSObject => 
    focused ? InputColors.focus({ cfg, theme }) : {};
    

const disabledControl = ({ disabled, isEmpty, theme, cfg }: Disable & Valueable & InputStyleConfigP): CSSObject => {
    const restDisableRule: CSSObject = { pointerEvents: 'none', '*': { opacity: '0.7' } };

    if(!disabled){
        return {};
    }

    if(isEmpty){
        return {
            ...restDisableRule,
            ...InputColors.disabledEmptyValue({ cfg, theme }),
        };
    }
    
    return {
        ...restDisableRule,
        ...InputColors.disabled({ cfg, theme }),
    };
}


interface ControlProps extends Focusable, Disable, Valueable {}


const Control_ = styled.div<ControlProps & InputStyleConfigP>`
    display: flex;
    cursor: text;

    ${InputColors.rest}
    ${InputColors.normal}

    &:hover {
        ${hoverControl}
    }

    ${focusedControl}
    ${disabledControl}
`;

export const Control = withConfig<ControlProps, HTMLDivElement>()(InputConfig)(Control_);
